import ApiWrapper from './ApiWrapper';
import Logger from '../Logging/Logger';
import { PaginatedRequest } from '../../domain/Common/PaginatedRequest';
import { PaginatedResponse } from '../../domain/Common/PaginatedResponse';
import { DtoImageUpdate } from '../../domain/Common/DtoImageUpdate';
import { DtoWithImageFile } from '../../domain/Common/DtoWithImageFile';
import { LightTokenGetterApi } from '../Common/LightTokenGetterApi';
import { authEvents } from './authEvents';

const REFRESH_RETRY_DELAYS_MS = [0, 500, 1500];
const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
let refreshInFlight: Promise<void> | null = null;

const refreshTokenWithLock = () => {
    if (!refreshInFlight) {
        refreshInFlight = LightTokenGetterApi.refreshToken().finally(() => {
            refreshInFlight = null;
        });
    }
    return refreshInFlight;
};

class ApiWrapperAuthenticated<TGet = void, 
                              TInsert = void, 
                              TUpdate = void, 
                              TDelete = void, 
                              TFilter extends PaginatedRequest | void = void,
                              TGetAnon = void,
                              TInsertAnon = void, 
                              TUpdateAnon = void, 
                              TDeleteAnon = void, 
                              TFilterAnon extends PaginatedRequest | void = void> 
    extends ApiWrapper<TGetAnon, TInsertAnon, TUpdateAnon, TDeleteAnon, TFilterAnon> {
    
    constructor(endpoint: string) {
        super(endpoint, true);
    }

    private getAuthHeadersAsync(): Record<string, string> {
        const accessToken = LightTokenGetterApi.getAccessToken();
        return {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/json',
        };
    }

    // Headers for authenticated FormData upload (exclude explicit Content-Type so browser sets boundary)
    private getAuthFormHeadersAsync(): Record<string, string> {
        const base = this.getAuthHeadersAsync();
        const { Authorization, Accept } = base;
        return { Authorization, Accept };
    }

    private async retryWithFreshToken<T>(
        request: (headers: Record<string, string>) => Promise<T | null>
    ): Promise<T | null> {
        let lastError: unknown = null;
        for (let i = 0; i < REFRESH_RETRY_DELAYS_MS.length; i++) {
            const delay = REFRESH_RETRY_DELAYS_MS[i];
            if (delay > 0) {
                await sleep(delay);
            }

            try {
                await refreshTokenWithLock();
            } catch (refreshError) {
                lastError = refreshError;
                Logger.error(`Error refreshing token (attempt ${i + 1}): ${refreshError}`);
                if (i === REFRESH_RETRY_DELAYS_MS.length - 1) {
                    authEvents.emit('sessionExpired');
                    throw new Error('Session expired. Please log in again.');
                }
                continue;
            }

            try {
                const headers = this.getAuthHeadersAsync();
                return await request(headers);
            } catch (retryError: any) {
                lastError = retryError;
                const retryStatus = retryError?.response?.status ?? retryError?.status;
                if (retryStatus !== 401) {
                    throw retryError;
                }

                if (i === REFRESH_RETRY_DELAYS_MS.length - 1) {
                    authEvents.emit('sessionExpired');
                    throw new Error('Session expired. Please log in again.');
                }
            }
        }

        if (lastError) {
            throw lastError;
        }

        authEvents.emit('sessionExpired');
        throw new Error('Session expired. Please log in again.');
    }

    private async handleAuthenticatedRequest<T>(
        request: (headers: Record<string, string>) => Promise<T | null>
    ): Promise<T | null> {
        try {
            const headers = this.getAuthHeadersAsync();
            return await request(headers);
        } catch (error: any) {
            const message = String(error?.message ?? '');
            const status = error?.response?.status ?? error?.status;

            // No token available — session is invalid, emit expiration gracefully
            if (message.includes('No access token available')) {
                authEvents.emit('sessionExpired');
                return null;
            }

            if (status === 401) {
                try {
                    return await this.retryWithFreshToken(request);
                } catch (refreshChainError: unknown) {
                    const refreshMsg = String((refreshChainError as Error)?.message ?? '');
                    if (refreshMsg.includes('Session expired')) {
                        // Session expiration already emitted; return gracefully
                        // so the error does not crash the React component tree.
                        return null;
                    }
                    this.handleError(refreshChainError);
                    return null;
                }
            }

            if (message.includes('Session expired')) {
                authEvents.emit('sessionExpired');
                return null;
            }
            this.handleError(error); // Use base error handler
            return null;
        }
    }

    protected async getOneAuthenticatedCustom<TParam, T>(options: {
        params?: TParam,
        url?: string,
        hasArrayParameter?: boolean
    }): Promise<T | null> {
        return await this.handleAuthenticatedRequest<T>((headers) =>
            this.getOneBase({...options, headers})
        );
    }

    public async getOneAuthenticated(): Promise<TGet | null> {
        return await this.getOneAuthenticatedCustom({});
    }

    protected async getListAuthenticatedCustom<TParam, T>(options: {
        params: TParam, 
        url?: string
    }): Promise<T[] | null> {
        return await this.handleAuthenticatedRequest<T[]>((headers) =>
            this.getListBase({...options, headers})
        );
    }

    public async getListAuthenticated<TParam>(params: TParam): Promise<TGet[] | null> {
        return await this.getListAuthenticatedCustom({params});
    }

    protected async getListAllAuthenticatedCustom<T>(options: {
        url?: string
    }): Promise<T[] | null> {
        return await this.handleAuthenticatedRequest<T[]>((headers) =>
            this.getListAllBase({...options, headers})
        );
    }

    public async getListAllAuthenticated(): Promise<TGet[] | null> {
        return await this.getListAllAuthenticatedCustom({});
    }

    protected async postAuthenticatedCustom<TInsertCustom, T>(options: {
        data: TInsertCustom, 
        url?: string
    }): Promise<T | null> {
        return await this.handleAuthenticatedRequest<T>((headers) =>
            this.postBase({...options, headers})
        );
    }

    // Generic authenticated FormData POST (custom payload including additional fields besides a file).
    // Expects caller to prepare the FormData instance.
    protected async postAuthenticatedFormDataCustom<T>(options: { formData: FormData; url?: string }): Promise<T | null> {
        return await this.handleAuthenticatedRequest<T>(async () => {
            const headers = this.getAuthFormHeadersAsync();
            // Reuse postBase infrastructure but bypass JSON content-type.
            // We call axios directly via postBase by faking data type as any.
            return await this.postBase<FormData, T>({ data: options.formData as any, url: options.url, headers });
        });
    }

    // Authenticated single file upload using key "file"
    protected async postAuthenticatedFileCustom<T>(options: { file: File; url?: string }): Promise<T | null> {
        return await this.handleAuthenticatedRequest<T>(async () => {
            const headers = this.getAuthFormHeadersAsync();
            return await this.postFileBase<T>({ ...options, headers });
        });
    }

    public async insertAuthenticatedFile<T>(file: File): Promise<T | null> {
        return await this.postAuthenticatedFileCustom<T>({ file });
    }

    public async existsAuthenticated(data: TInsert): Promise<boolean | null> {
        return await this.getOneAuthenticatedCustom({ url: 'exists', params: data });
    }

    public async existsUpdateAuthenticated(data: TUpdate): Promise<boolean | null> {
        return await this.getOneAuthenticatedCustom({ url: 'exists-update', params: data });
    }

    public async insertAuthenticated<T>(data: TInsert): Promise<T | null> {
        return await this.postAuthenticatedCustom({data});
    }

    protected async putAuthenticatedCustom<TUpdateCustom, T>(options: {
        data: TUpdateCustom, 
        url?: string
    }): Promise<T | null> {
        return await this.handleAuthenticatedRequest<T>((headers) =>
            this.putBase({...options, headers})
        );
    }

    public async updateAuthenticated<T>(data: TUpdate): Promise<T | null> {
        return await this.putAuthenticatedCustom({data});
    }

    protected async deleteAuthenticatedCustom<TParam>(options: {
        params: TParam, 
        url?: string
    }): Promise<void> {
        await this.handleAuthenticatedRequest<void>((headers) =>
            this.deleteBase({...options, headers})
        );
    }

    public async deleteAuthenticated(params: TDelete): Promise<void> {
        await this.deleteAuthenticatedCustom({params});
    }

    protected async deleteAuthenticatedNoParamsCustom(options: {
        url?: string
    }): Promise<void> {
        await this.handleAuthenticatedRequest<void>((headers) =>
            this.deleteBase({...options, headers})
        );
    }

    public async deleteAuthenticatedNoParams(): Promise<void> {
        await this.deleteAuthenticatedNoParamsCustom({});
    }

    protected async downloadExcelAuthenticated<TParam>(options: {
        url: string, 
        filename: string,
        params?: TParam
    }): Promise<boolean | null> {
        return await this.handleAuthenticatedRequest<boolean | null>((headers) => 
            this.downloadExcelBase({...options, headers})
       );
    }

    protected async fetchPaginatedAuthenticatedCustom<T, TFilt extends PaginatedRequest>(options: {
        data: TFilt, 
        url?: string
    }): Promise<PaginatedResponse<T[]> | null> {
        return await this.postAuthenticatedCustom({...options, url: options.url || 'search-paged'});
    }

    public async fetchPaginatedAuthenticated(request: TFilter): Promise<PaginatedResponse<TGet[]> | null> {
        return await this.fetchPaginatedAuthenticatedCustom({ data: request as any});
    }
    
    public async updateImageAuthenticated(imgUpdate: DtoImageUpdate) : Promise<void> {
        await this.putAuthenticatedCustom({
            url: 'image',
            data: this.updateImageGetPayload(imgUpdate)
    });
    }

    public async insertImageAuthenticated(formDataWithImage: TInsert) : Promise<void> {
        var casted = formDataWithImage as DtoWithImageFile<any>;
        await this.postAuthenticatedCustom({data: this.dtoWithImageGetPayload(casted)});
    }

    public async updateWithImageAuthenticated(formDataWithImage: TUpdate) : Promise<void> {
        var casted = formDataWithImage as DtoWithImageFile<any>;
        await this.putAuthenticatedCustom({ data: this.dtoWithImageGetPayload(casted) });
    }
}

export default ApiWrapperAuthenticated;
